import { InventoryItem } from '@/root/shared-types'
import { getInventoryValues } from '@/root/shared-helpers/sail-select/getInventoryValues'
import { getResidentInventoryValues } from '@/root/shared-helpers/sail-select/getResidentInventoryValues'

export type UnavailableReason = 'passengers' | 'vehicles' | 'bicycles'

export interface SailingAvailabilityResult {
  isAvailable: boolean
  unavailableReasons: UnavailableReason[]
}

export interface RequiredCounts {
  passengers: number
  residentPassengers: number
  vehicles: number
  residentVehicles: number
  bicycles: number
}

/**
 * A class fits when non-resident demand fits the general inventory AND total demand
 * fits general + resident reserve. Residents may draw from general + reserve; regular
 * tickets only from general. The allocation order (general-first vs reserve-first) does
 * not affect feasibility, so this holds for both Kihnu and tuuleliinid.
 */
const fitsClass = (total: number, resident: number, general: number, reserve: number): boolean => {
  const regular = total - resident
  return regular <= general && total <= general + reserve
}

export const checkSailingAvailability = (item: InventoryItem, required: RequiredCounts): SailingAvailabilityResult => {
  const unavailableReasons: UnavailableReason[] = []

  if (required.passengers > 0) {
    const { amount: deckAmount = 0 } = getInventoryValues({
      deckAvailability: item.passengerDeck,
      inventoryClass: 'DECK',
    })
    const { amount: residentReserve = 0 } = getResidentInventoryValues(item.passengerDeck)

    if (!fitsClass(required.passengers, required.residentPassengers, deckAmount, residentReserve)) {
      unavailableReasons.push('passengers')
    }
  }

  if (required.vehicles > 0) {
    const { amount: carAmount = 0 } = getInventoryValues({
      deckAvailability: item.carDeck,
      inventoryClass: 'CAR_DECK',
    })
    const { amount: carResidentReserve = 0 } = getResidentInventoryValues(item.carDeck, 'CAR_DECK')

    if (!fitsClass(required.vehicles, required.residentVehicles, carAmount, carResidentReserve)) {
      unavailableReasons.push('vehicles')
    }
  }

  if (required.bicycles > 0) {
    const { amount: bicycleAmount = 0 } = getInventoryValues({
      deckAvailability: item.carDeck,
      inventoryClass: 'BICYCLE',
    })

    if (required.bicycles > bicycleAmount) {
      unavailableReasons.push('bicycles')
    }
  }

  return {
    isAvailable: unavailableReasons.length === 0,
    unavailableReasons,
  }
}
